import React from 'react';
import { ExternalLink, Globe, Image as ImageIcon } from 'lucide-react';
import styles from './WebSearchResults.module.css';

export interface SearchResult {
  title: string;
  description: string;
  url: string;
  favicon: string;
  image?: string;
  position: number;
}

interface WebSearchResultsProps {
  query?: string;
  results: SearchResult[];
  isLoading?: boolean;
  compact?: boolean;
}

export const WebSearchResults: React.FC<WebSearchResultsProps> = ({
  query,
  results,
  isLoading,
  compact = false,
}) => {
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Globe size={16} className={styles.searchingIcon} />
          <span>{query ? `Searching for "${query}"...` : 'Searching...'}</span>
        </div>
        <div className={styles.loadingGrid}>
          {[1, 2, 3].map((i) => (
            <div key={i} className={styles.skeletonCard}>
              <div className={styles.skeletonTitle} />
              <div className={styles.skeletonDesc} />
              <div className={styles.skeletonUrl} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.noResults}>
          {query ? `No results found for "${query}"` : 'No results found'}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${compact ? styles.compact : ''}`}>
      <div className={styles.header}>
        <Globe size={16} />
        <span>{query ? `Web results for "${query}"` : 'Web results'}</span>
        <span className={styles.resultCount}>{results.length} sources</span>
      </div>
      <div className={styles.resultsGrid}>
        {results.map((result) => (
          <SearchResultCard key={result.url} result={result} compact={compact} />
        ))}
      </div>
    </div>
  );
};

interface SearchResultCardProps {
  result: SearchResult;
  compact?: boolean;
}

const SearchResultCard: React.FC<SearchResultCardProps> = ({ result, compact }) => {
  const domain = extractDomain(result.url);

  return (
    <a
      href={result.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`${styles.card} ${compact ? styles.compactCard : ''}`}
    >
      {result.image && !compact && (
        <div className={styles.imageContainer}>
          <img
            src={result.image}
            alt=""
            className={styles.previewImage}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      )}
      <div className={styles.cardContent}>
        <div className={styles.cardHeader}>
          {result.favicon ? (
            <img
              src={result.favicon}
              alt=""
              className={styles.favicon}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <Globe size={14} className={styles.faviconFallback} />
          )}
          <span className={styles.domain}>{domain}</span>
          <ExternalLink size={12} className={styles.externalIcon} />
        </div>
        <h4 className={styles.title}>{result.title}</h4>
        {!compact && <p className={styles.description}>{result.description}</p>}
      </div>
    </a>
  );
};

function extractDomain(url: string): string {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return url;
  }
}

// Inline search result for AI messages
export const InlineSearchResult: React.FC<{ result: SearchResult }> = ({ result }) => {
  const domain = extractDomain(result.url);

  return (
    <a href={result.url} target="_blank" rel="noopener noreferrer" className={styles.inlineResult}>
      {result.favicon ? (
        <img src={result.favicon} alt="" className={styles.inlineFavicon} />
      ) : (
        <Globe size={12} />
      )}
      <span className={styles.inlineTitle}>{result.title}</span>
      <span className={styles.inlineDomain}>({domain})</span>
    </a>
  );
};
